<template>
  <div class="article-details">
    <!-- Header avec navigation et actions -->
    <div class="header">
      <div class="header__left">
        <button class="btn-back" @click="goBack">
          <span class="icon">←</span>
          {{ article.reference }}
        </button>
        <StatusCell :status="article.status" :hideInfoIcon="true" />
      </div>
      <div class="header__right">
        <!-- Plus d'actions : regroupe les actions secondaires sur l'article -->
        <div
          v-if="hasMoreActions"
          class="more-actions"
          v-click-outside="closeMoreActions"
        >
          <button
            class="btn btn-secondary btn-icon-only"
            :aria-label="$t('article_put_online_details.buttons.more_actions')"
            :aria-expanded="String(isMoreActionsOpen)"
            @click="toggleMoreActions"
          >
            <SvgIcon name="dots" />
          </button>

          <div v-if="isMoreActionsOpen" class="more-actions__menu">
            <button
              v-for="action in moreActions"
              :key="action.key"
              class="more-actions__option"
              :class="{
                'more-actions__option--warning': action.warning,
              }"
              :disabled="action.disabled"
              @click="runMoreAction(action)"
            >
              <SvgIcon class="more-actions__icon" :name="action.icon" />
              <span>{{ action.label }}</span>
              <span v-if="action.beta" class="ai-photo-beta-badge">
                {{ $t("article_put_online_details.buttons.ai_photos_beta") }}
              </span>
            </button>
          </div>
        </div>

        <!-- Enregistrer : actif uniquement s'il y a des modifications en attente -->
        <button
          v-if="canEditPhotos"
          class="btn btn-secondary"
          :disabled="!hasUnsavedChanges || saveLoading"
          @click="handleSave"
        >
          {{ $t("article_put_online_details.buttons.save") }}
        </button>

        <!-- Publier : disabled si statut === 'En vente'.
             Volontairement sur `isClient` et non `canEditArticleSheet` : une marque
             édite le contenu de la fiche, la mise en ligne reste côté Faume. -->
        <button
          v-if="!isClient"
          class="btn btn-primary"
          :disabled="isOnSale || hasUnsavedChanges"
          @click="handlePublish"
        >
          {{ $t("article_put_online_details.buttons.published") }}
        </button>

        <!-- Nouveau bouton -->
        <div class="btn-nav">
          <div class="btn-nav">
            <!-- Bouton précédent encapsulé dans le composant CustomTooltip -->
            <CustomTooltip
              :disabled="
                isClient &&
                !(savedFilters.stockDate || savedFilters.publicationDate)
              "
              :title="$t('article_put_online_details.tooltips.filters_apply')"
              position="top"
            >
              <!-- Bouton déclencheur -->
              <template #default>
                <button
                  class="btn btn-secondary"
                  @click="goToPreviousProduct"
                  :disabled="isFirstProduct || hasUnsavedChanges"
                  ref="prevBtn"
                >
                  <SvgIcon name="left" />
                </button>
              </template>
              <!-- Contenu du tooltip -->
              <template #content>
                <div v-if="hasActiveFilters">
                  <div v-if="savedFilters.brands">
                    <!-- Affiche la marque -->
                    <StatusCell :status="getBrandsLabel" :hideInfoIcon="true" />
                  </div>
                  <div v-if="currentTab && currentTab !== 'all' && !isClient">
                    <!-- Affiche le statut ou le tab -->
                    <StatusCell :status="getStatusFromTab(currentTab)" :hideInfoIcon="true" />
                  </div>
                  <div v-if="savedFilters.stockDate">
                    <!-- Affiche la date de mise en stock -->
                    <StatusCell :status="stockDateDisplay" :hideInfoIcon="true" />
                  </div>
                  <div v-if="savedFilters.publicationDate">
                    <!-- Affiche la date de publication -->
                    <StatusCell :status="publicationDateDisplay" :hideInfoIcon="true" />
                  </div>
                  <div v-if="savedFilters.errors">
                    <!-- Affiche les erreurs -->
                    <StatusCell
                      :status="`Errors: ${savedFilters.errors.values.join(
                        ', '
                      )}`"
                      :hideInfoIcon="true"
                    />
                  </div>
                </div>
                <div v-else>
                  {{
                    $t("article_put_online_details.tooltips.filters_no_apply")
                  }}
                </div>
              </template>
            </CustomTooltip>

            <!-- Bouton suivant encapsulé dans CustomTooltip -->
            <CustomTooltip
              :disabled="
                isClient &&
                !(savedFilters.stockDate || savedFilters.publicationDate)
              "
              :title="$t('article_put_online_details.tooltips.filters_apply')"
              position="top"
            >
              <template #default>
                <button
                  class="btn btn-secondary"
                  @click="goToNextProduct"
                  :disabled="hasUnsavedChanges"
                  ref="nextBtn"
                >
                  <SvgIcon name="right" />
                </button>
              </template>
              <template #content>
                <div v-if="hasActiveFilters">
                  <div v-if="savedFilters.brands">
                    <StatusCell :status="getBrandsLabel" :hideInfoIcon="true" />
                  </div>
                  <div v-if="currentTab && currentTab !== 'all' && !isClient">
                    <StatusCell :status="getStatusFromTab(currentTab)" :hideInfoIcon="true" />
                  </div>
                  <div v-if="savedFilters.stockDate">
                    <StatusCell :status="stockDateDisplay" :hideInfoIcon="true" />
                  </div>
                  <div v-if="savedFilters.publicationDate">
                    <StatusCell :status="publicationDateDisplay" :hideInfoIcon="true" />
                  </div>
                  <div v-if="savedFilters.errors">
                    <StatusCell
                      :status="`Errors: ${savedFilters.errors.values.join(
                        ', '
                      )}`"
                      :hideInfoIcon="true"
                    />
                  </div>
                </div>
                <div v-else>
                  {{
                    $t("article_put_online_details.tooltips.filters_no_apply")
                  }}
                </div>
              </template>
            </CustomTooltip>
          </div>
        </div>

        <!-- Modal de signalement -->
        <BaseModal
          v-if="isReportModalOpen"
          :title="$t('article_put_online_details.report_modal.title')"
          :subtitle="$t('article_put_online_details.report_modal.subtitle')"
          @close="handleReportClose"
          @submit="triggerReportSubmit"
          :submitDisabled="reportFormDisabled"
          :submitText="$t('article_put_online_details.report_modal.submit')"
          :cancelText="$t('article_put_online_details.report_modal.cancel')"
        >
          <template v-slot:content>
            <ReportProblemForm
              ref="reportForm"
              :article-id="articleData?.articleCard?.id || ''"
              :existing-errors="article.reportReasons"
              @update:submitDisabled="reportFormDisabled = $event"
              @submit="handleReportSubmit"
            />
          </template>
        </BaseModal>

        <ArticleCardAiPhotoModal
          v-if="isAiPhotoModalOpen && articleCardId && clientId"
          :article-card-id="articleCardId"
          :client-id="clientId"
          :is-faume-admin="isAdmin"
          @close="closeAiPhotoModal"
          @applied="handleAiPhotoApplied"
        />

        <!-- Confirmation avant de quitter la page avec des saisies en attente -->
        <BaseModal
          v-if="isLeaveConfirmOpen"
          :title="$t('article_put_online_details.leave_modal.title')"
          :cancelText="$t('article_put_online_details.leave_modal.cancel')"
          :submitText="$t('article_put_online_details.leave_modal.confirm')"
          confirmButtonClass="btn-red"
          @close="cancelLeave"
          @submit="confirmLeave"
        >
          <template v-slot:content>
            <p class="leave-modal__message">
              {{ $t("article_put_online_details.leave_modal.message") }}
            </p>
          </template>
        </BaseModal>
      </div>
    </div>
    <div class="info-update">
      <div class="update-date">
        <span class="update-label"
          >{{
            $t("article_put_online_details.last_update") ||
            "Dernière mise à jour"
          }}:</span
        >
        <span class="update-value">{{
          articleData?.articleCard?.updatedAt
            ? formatRelativeTime(articleData.articleCard.updatedAt)
            : "-"
        }}</span>
      </div>
    </div>
    <!-- Language selection -->
    <div class="tabs-wrapper">
      <div class="tabs">
        <button
          v-for="lang in languages"
          :key="lang.code"
          @click="handleLangChange(lang.code)"
          :class="['tab-button', { active: lang.code === currentLang }]"
        >
          <img :src="lang.flag" :alt="lang.name" class="lang-flag" />
          {{ lang.name }}
        </button>
      </div>
    </div>

    <!-- Main content -->
    <div class="content-wrapper">
      <!-- Left column -->
      <div class="main-content">
        <div class="section">
          <div class="section_header">
            <h2>
              {{ $t("article_put_online_details.section.controled.title") }}
            </h2>
          </div>
          <div class="section_containt">
            <div class="form-group">
              <div class="input-container" :class="{ disabled: isReadOnly }">
                <label>{{
                  $t("article_put_online_details.section.controled.input.title")
                }}</label>
                <textarea
                  v-model="draft.title"
                  ref="titleTextarea"
                  :disabled="isReadOnly"
                ></textarea>
              </div>
            </div>

            <div class="form-group">
              <div class="input-container" :class="{ disabled: isReadOnly }">
                <label>{{
                  $t(
                    "article_put_online_details.section.controled.input.description"
                  )
                }}</label>
                <vue-editor
                  v-model="draft.description"
                  :editor-toolbar="customToolbar"
                  :disabled="isReadOnly"
                />
              </div>
            </div>

            <div class="form-group">
              <div class="input-container" :class="{ disabled: isReadOnly }">
                <label>{{
                  $t(
                    "article_put_online_details.section.controled.input.material"
                  )
                }}</label>
                <textarea
                  v-model="draft.material"
                  ref="materialTextarea"
                  :disabled="isReadOnly"
                ></textarea>
              </div>
            </div>

            <div class="form-group">
              <div class="input-container disabled">
                <label>
                  {{
                    $t(
                      "article_put_online_details.section.controled.input.priceResaleBeforeDiscount"
                    )
                  }}
                </label>
                <input type="text" :value="formattedPriceResale" disabled />
              </div>
            </div>

            <div class="form-group" v-if="formattedPriceResaleDiscounted">
              <div class="input-container disabled">
                <label>
                  {{
                    $t(
                      "article_put_online_details.section.controled.input.priceResaleAfterDiscount"
                    )
                  }}
                </label>
                <input
                  type="text"
                  :value="formattedPriceResaleDiscounted"
                  disabled
                />
              </div>
            </div>

            <div class="form-group">
              <div class="input-container" :class="{ disabled: true }">
                <label>{{
                  $t("article_put_online_details.section.controled.input.color")
                }}</label>
                <input type="text" v-model="article.color" disabled />
              </div>
            </div>

            <div class="form-group">
              <div class="input-container" :class="{ disabled: true }">
                <label>{{
                  $t(
                    "article_put_online_details.section.controled.input.collection"
                  )
                }}</label>
                <input type="text" v-model="article.collection" disabled />
              </div>
            </div>

            <div class="section_containt-photos">
              <h2>
                {{
                  $t(
                    "article_put_online_details.section.controled.input.photos"
                  )
                }}
              </h2>
              <PhotosManager
                :photos="draft.photos"
                :article-id="article.id"
                :read-only="!canEditPhotos"
                @update:photos="handlePhotosUpdate"
              />
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section_header">
            <h2>
              {{
                $t(
                  "article_put_online_details.section.additional_informations.title"
                )
              }}
            </h2>
          </div>
          <div class="section_containt">
            <div class="info-list">
              <div v-if="article?.gender" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.Genre"
                  )
                }}</label>
                <span>{{ article?.gender }}</span>
              </div>
              <div v-if="article?.category" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.category"
                  )
                }}</label>
                <span>{{ article?.category }}</span>
              </div>
              <div v-if="article?.univers" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.Univers"
                  )
                }}</label>
                <span>{{ article?.univers }}</span>
              </div>
              <div v-if="article?.type" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.type"
                  )
                }}</label>
                <span>{{ article?.type }}</span>
              </div>
              <div v-if="article?.season" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.Season"
                  )
                }}</label>
                <span>{{ article?.season }}</span>
              </div>
              <div v-if="article?.year" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.year"
                  )
                }}</label>
                <span>{{ article?.year }}</span>
              </div>
              <div v-if="article?.colorSpecific" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.specific_color"
                  )
                }}</label>
                <span>{{ article?.colorSpecific }}</span>
              </div>
              <div v-if="article?.color" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.color_filters"
                  )
                }}</label>
                <span>{{ article?.color }}</span>
              </div>
              <div v-if="article?.size" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.size"
                  )
                }}</label>
                <span>{{ article?.size }}</span>
              </div>
              <div v-if="article?.sizeFilter" class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.additional_informations.size_filters"
                  )
                }}</label>
                <span>{{ article?.sizeFilter }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right column -->
      <div class="side-content">
        <div class="section">
          <div class="section_header">
            <h2>
              {{
                $t("article_put_online_details.section.item_informations.title")
              }}
            </h2>
          </div>
          <div class="section_containt">
            <div class="info-list">
              <div class="info-row">
                <label>ID</label>
                <span>{{ article.reference }}</span>
              </div>
              <div class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.item_informations.brand"
                  )
                }}</label>
                <span>{{ article.brand }}</span>
              </div>
              <div class="info-row">
                <label>EAN</label>
                <span>{{ article.ean }}</span>
              </div>
              <div class="info-row">
                <label>SKU</label>
                <span>{{ article.sku }}</span>
              </div>
              <div class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.item_informations.in-stock"
                  )
                }}</label>
                <span>{{ stockDate }}</span>
              </div>
              <div class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.item_informations.publication"
                  )
                }}</label>
                <span>{{ publishDate }}</span>
              </div>
              <div class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.item_informations.item_condition"
                  )
                }}</label>
                <span>{{ $t("global." + article.condition) }}</span>
              </div>
              <div class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.item_informations.item_status"
                  )
                }}</label>
                <StatusCell :status="article.status" :hideInfoIcon="true" />
              </div>
              <!-- Erreur(s) à corriger pour to_correct -->
              <div v-if="isToCorrect" class="info-row report-reasons">
                <label>
                  {{
                    $t(
                      "article_put_online_details.section.item_informations.report_reasons"
                    )
                  }}</label
                >
                <div class="error-tags">
                  <ErrorsCell :errors="uniqueReportReasons" />
                </div>
              </div>

              <!-- Raison de blocage pour draft -->
              <div v-if="isDraft && article.draftReason" class="info-row report-reasons">
                <label>
                  {{
                    $t(
                      "article_put_online_details.section.item_informations.draft_reason"
                    )
                  }}</label
                >
                <div class="error-tags">
                  <ErrorsCell :errors="[{ reason: article.draftReason, isDraftReason: true }]" />
                </div>
              </div>

              <div class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.item_informations.see_item"
                  )
                }}</label>
                <a
                  :href="
                    article.id
                      ? $router.resolve({
                          name: 'recom_tradein-n-sell-article',
                          params: { id: article.id },
                        }).href
                      : null
                  "
                  target="_blank"
                  class="view-link"
                >
                  {{
                    $t(
                      "article_put_online_details.section.item_informations.see_item_link"
                    )
                  }}
                </a>
              </div>
              <div class="info-row">
                <label>{{
                  $t(
                    "article_put_online_details.section.item_informations.see_item_online"
                  )
                }}</label>
                <a
                  v-if="article.articleFrontUrl && article.status === 'on_sale'"
                  :href="formattedArticleFrontUrl"
                  target="_blank"
                  class="view-link"
                >
                  {{
                    $t(
                      "article_put_online_details.section.item_informations.see_item_online_link"
                    )
                  }}
                </a>
                <span v-else>-</span>
              </div>
              <!-- Add other info rows -->
            </div>
          </div>
        </div>
      </div>
    </div>
    <PopinNotification ref="notification" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { VueEditor } from "vue2-editor";
import currencyRouteMixin from "@/mixins/currencyRouteMixin";
import dateFormatMixin from "@/mixins/dateFormatMixin";
import priceFormatMixin from "@/mixins/priceFormatMixin";

import PopinNotification from "@/components/ui/popin/PopinNotification.vue";
import BaseModal from "@/components/ui/modals/BaseModal.vue"; // Ajustez le chemin d'import selon votre structure
import ReportProblemForm from "@/components/ui/dataTable/ReportProblemForm.vue";
import StatusCell from "@/components/ui/dataTable/StatusCell.vue";
import PhotosManager from "@/components/photo/PhotosManager";
import ArticleCardAiPhotoModal from "@/components/photo/ArticleCardAiPhotoModal.vue";
import SvgIcon from "@/components/ui/svg/SvgIcon.vue";
import ErrorsCell from "@/components/ui/dataTable/ErrorsCell.vue";
import CustomTooltip from "@/components/ui/tootip/CustomTooltip.vue";
export default {
  name: "ConfigWebsiteCatalogueArticleDetailsBis",

  components: {
    PhotosManager,
    VueEditor,
    PopinNotification,
    StatusCell,
    SvgIcon,
    BaseModal,
    ReportProblemForm,
    ErrorsCell,
    CustomTooltip,
    ArticleCardAiPhotoModal,
  },
  mixins: [currencyRouteMixin, dateFormatMixin, priceFormatMixin],

  data() {
    return {
      currentLang: null,
      customToolbar: [
        ["bold", "italic"],
        [{ list: "bullet" }, { list: "ordered" }],
      ],
      // Tampon d'édition : les saisies vivent ici et ne sont envoyées au back
      // qu'au clic sur "Enregistrer" (une seule requête pour tous les champs).
      draft: {
        title: "",
        description: "",
        material: "",
        photos: [],
      },
      // Copie de référence servant au calcul de `hasUnsavedChanges`.
      draftBaseline: {
        title: "",
        description: "",
        material: "",
        photos: [],
      },
      // Modifications texte des langues autres que `currentLang`, accumulées
      // lors des changements d'onglet et envoyées avec le reste au save.
      pendingMetadataByLang: {},
      saveLoading: false,
      isMoreActionsOpen: false,
      isLeaveConfirmOpen: false,
      // `next` du beforeRouteLeave mis en attente le temps que l'utilisateur
      // réponde dans la modale (le hook est synchrone, la modale ne l'est pas).
      pendingLeaveNext: null,
      savedFilters: {},
      savedSort: null,
      savedSearch: null,
      savedLimit: 100,
      isReportModalOpen: false,
      reportFormDisabled: true, // État du bouton submit du formulaire de signalement
      syncLoading: false, // État de chargement pour la synchronisation
      isAiPhotoModalOpen: false,
    };
  },

  computed: {
    ...mapState({
      loading: (state) => state.articleLoading,
      articleData: (state) => state.article,
      allProducts: (state) => state.articles?.items || [],
      tableFilters: (state) => state.articlesFilters || [],
    }),

    ...mapGetters([
      "isClient",
      "roleIsAllowed",
      "isAdmin",
      "canEditArticleSheet",
    ]),

    // Le bouton "Enregistrer" est masqué pour qui ne peut pas éditer : sans cette
    // garde l'utilisateur saisirait des modifications qu'aucune action ne peut
    // persister, et `hasUnsavedChanges` grisserait tous les autres CTA de la page.
    isReadOnly() {
      return !this.canEditArticleSheet;
    },

    canEditPhotos() {
      return !this.isReadOnly || this.roleIsAllowed(["ROLE_PHOTO_EDIT"]);
    },

    canAccessAiPhoto() {
      return this.roleIsAllowed(["ROLE_PHOTO_AI", "ROLE_ADMIN"]);
    },

    articleCardId() {
      return this.articleData?.articleCard?.id || null;
    },

    clientId() {
      return this.articleData?.brand?.id || null;
    },

    articleId() {
      return this.$route.params.id;
    },

    article() {
      if (!this.articleData) {
        return {
          id: "",
          title: "",
          description: "",
          material: "",
          priceResale: "",
          priceResaleForced: "",
          discountedPrice: "",
          color: "",
          collection: "",
          status: "",
          condition: "",
          brand: "",
          ean: "",
          photos: [],
          sku: "",
          gender: "",
          category: "",
          univers: "",
          type: "",
          season: "",
          year: "",
          colorSpecific: "",
          size: "",
          sizeFilter: "",
          articleFrontUrl: "",
          reportReasons: [],
          draftReason: null,
        };
      }

      const currentMetadata = this.articleData.metadata?.[this.currentLang];

      return {
        id: this.articleData.id,
        reference: this.articleData.reference,
        title: currentMetadata?.title || "",
        description: currentMetadata?.description || "",
        material: currentMetadata?.composition || "",
        priceResale: this.articleData.priceResale || "",
        priceResaleForced: this.articleData.priceResaleForced || "",
        discountedPrice: this.articleData.discountedPrice || "",
        color: currentMetadata?.color_filters || "",
        colorSpecific: currentMetadata?.color || "",
        collection: currentMetadata?.collection || "",
        status: this.articleData.articleCard?.state || "",
        condition: this.articleData.condition || "",
        brand: this.articleData.brand?.name || "",
        ean: this.articleData.ean || "",
        sku: this.articleData.sku || "",
        gender: currentMetadata?.gender || "",
        category: currentMetadata?.category || "",
        type: currentMetadata?.type || "",
        univers: currentMetadata?.univers || "",
        season: currentMetadata?.season || "",
        year: currentMetadata?.year || "",
        size: currentMetadata?.size || "",
        sizeFilter: currentMetadata?.size_filters || "",
        articleFrontUrl: this.articleData.articleFrontUrl || "",
        photos:
          this.articleData?.photos?.map((photo) => ({
            url: photo.url,
            id: photo.id,
            isPrimary: false,
            position: photo.position,
            deletedAt: photo.deletedAt,
          })) || [],
        reportReasons: this.articleData.report_reasons || [],
        draftReason: this.articleData.draft_reason || null,
      };
    },

    hasUnsavedChanges() {
      if (!this.articleData) return false;

      const photosChanged =
        this.serializePhotos(this.draft.photos) !==
        this.serializePhotos(this.draftBaseline.photos);

      // Consultation seule : seul le masquage de photos peut produire des changements.
      if (this.isReadOnly) {
        return this.canEditPhotos && photosChanged;
      }

      const textChanged = ["title", "description", "material"].some(
        (field) => this.draft[field] !== this.draftBaseline[field]
      );

      // Une langue non affichée peut porter des modifications en attente.
      const otherLangChanged = Object.keys(this.pendingMetadataByLang).length > 0;

      return textChanged || photosChanged || otherLangChanged;
    },

    // Actions secondaires regroupées dans le menu "Plus d'actions". Les
    // conditions de visibilité et de désactivation sont celles qui
    // s'appliquaient aux boutons correspondants du header.
    // Volontairement sur `isClient` et non `canEditArticleSheet` : ces actions
    // (mise en ligne, dépublication, synchronisation, signalement) restent côté
    // Faume, même pour une marque autorisée à éditer le contenu de la fiche.
    moreActions() {
      const actions = [];

      if (!this.isClient && this.isToCorrect) {
        actions.push({
          key: "to_publish",
          label: this.$t("article_put_online_details.buttons.to_publish"),
          icon: "publish",
          handler: this.handleSetToPublish,
        });
      }

      if (this.canAccessAiPhoto) {
        actions.push({
          key: "ai_photos",
          label: this.$t("article_put_online_details.buttons.ai_photos"),
          icon: "stars",
          beta: true,
          handler: this.openAiPhotoModal,
        });
      }

      if (!this.isClient) {
        actions.push({
          key: "report",
          label: this.$t("article_put_online_details.buttons.error_reported"),
          icon: "info",
          handler: this.openReportModal,
        });
      }

      // Resynchroniser quel que soit le statut : récupérer les photos catalogue
      // a autant de sens sur une FP en vente ou dépubliée que sur un brouillon.
      // La whitelist draft/to_publish/to_correct d'origine retirait l'entrée du
      // menu au lieu de la griser, d'où un CTA introuvable.
      if (!this.isClient) {
        actions.push({
          key: "sync",
          label: this.$t("article_put_online_details.buttons.sync"),
          icon: "refresh",
          disabled: this.syncLoading,
          handler: this.handleSync,
        });
      }

      if (!this.isClient) {
        actions.push({
          key: "unpublish",
          label: this.$t("article_put_online_details.buttons.unpublished"),
          icon: "hide",
          warning: true,
          disabled:
            this.isUnpublished ||
            this.isDraft ||
            this.isToPublish ||
            this.isToCorrect,
          handler: this.handleUnpublish,
        });
      }

      return actions;
    },

    hasMoreActions() {
      return this.moreActions.length > 0;
    },

    uniqueReportReasons() {
      const reasons = this.article?.reportReasons || [];
      return Object.values(
        reasons.reduce((acc, curr) => {
          if (!acc[curr.reason]) {
            acc[curr.reason] = curr;
          }
          return acc;
        }, {})
      );
    },

    defaultLanguage() {
      return this.articleData?.brand?.defaultLocaleCode || "fr_FR";
    },

    languages() {
      if (!this.articleData?.metadata) return [];

      // Certains articles ont encore un metadata plat (legacy, non localisé) :
      // on ne garde que les clés au format locale (ex: fr_FR)
      const langs = Object.keys(this.articleData.metadata)
        .filter((key) => /^[a-z]{2}_[A-Z]{2}$/.test(key))
        .map((code) => ({
          code,
          name: this.$t(`locales.${code.split("_")[0].toLowerCase()}`),
          flag: `https://flagcdn.com/${code.split("_")[1].toLowerCase()}.svg`,
        }));

      // Trier les langues pour mettre la langue par défaut en premier
      return langs.sort((a, b) => {
        if (a.code === this.defaultLanguage) return -1;
        if (b.code === this.defaultLanguage) return 1;
        return 0;
      });
    },

    formattedPriceResale() {
      if (!this.article.priceResale) return "";
      const realPriceResale = (this.article.priceResaleForced && this.article.priceResaleForced > 0)
        ? this.article.priceResaleForced
        : this.article.priceResale;
      return this.formatPriceFromCents(realPriceResale);
    },

    priceResaleBeforeDiscount() {
      if (!this.article.priceResale) return 0;
      return (this.article.priceResaleForced && this.article.priceResaleForced > 0)
        ? this.article.priceResaleForced
        : this.article.priceResale;
    },

    formattedPriceResaleDiscounted() {
      // Affiché uniquement si une réduction est appliquée, c'est-à-dire si le
      // prix après réduction diffère du prix de vente seconde main avant réduction.
      const discounted = this.article.discountedPrice;
      if (!discounted || discounted === this.priceResaleBeforeDiscount) return "";
      return this.formatPriceFromCents(discounted);
    },

    formattedArticleFrontUrl() {
      let url = this.article.articleFrontUrl || "";
      const defaultLocale = this.defaultLanguage;
      const selectedLocale = this.currentLang;

      // Fonction pour supprimer les accents
      const removeAccents = (str) => {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      };

      // Supprimer les accents de l'URL
      url = removeAccents(url);

      try {
        // Analyser l'URL correctement
        const parsedUrl = new URL(url);

        // Obtenir le chemin de l'URL
        let path = parsedUrl.pathname;

        // Retirer le code de langue s'il existe
        path = path.replace(/\/[A-Za-z]{2}_[A-Za-z]{2}(\/|$)/, "/");

        // Si le chemin contient des segments après "products/"
        if (path.includes("/products/")) {
          // Diviser le chemin en segments
          const pathSegments = path.split("/");

          // Reconstruire le chemin avec les segments appropriés
          // 0: '', 1: 'products', 2+: autres segments
          let basePath = "/" + pathSegments[1] + "/"; // '/products/'

          // Joindre les autres segments avec des tirets uniquement s'ils contiennent des '/'
          const remainingSegments = pathSegments.slice(2).join("/");

          // Remplacer les '/' par des '-' uniquement dans les segments restants
          const formattedSegments = remainingSegments.replace(/\//g, "-");

          // Construire le nouveau chemin
          path = basePath + formattedSegments;
        }

        // Mettre à jour le chemin de l'URL
        parsedUrl.pathname = path;

        // Ajouter le code de langue si nécessaire
        if (selectedLocale !== defaultLocale) {
          parsedUrl.pathname = `/${selectedLocale}${parsedUrl.pathname}`;
        }

        return parsedUrl.toString();
      } catch (e) {
        console.error("Erreur lors du formatage de l'URL:", e);
        return url; // En cas d'erreur, retourner l'URL d'origine
      }
    },
    currentProductIndex() {
      return this.allProducts.findIndex(
        (product) => product.reference === this.articleId
      );
    },

    isFirstProduct() {
      const currentIndex = this.allProducts.findIndex(
        (item) => item.id === this.articleId
      );
      return currentIndex === 0;
    },

    stockDate() {
      if (!this.articleData?.activities?.length) return null;

      // On trouve la dernière activité 'stocked'
      const stockActivity = [...this.articleData.activities]
        .reverse()
        .find((activity) => activity.event === "stocked");

      return stockActivity
        ? this.formatDateForDisplay(stockActivity.createdAt)
        : "-";
    },

    stockDateDisplay() {
      const sd = this.savedFilters.stockDate;
      if (!sd || !sd.value1) return "";
      const date1 = this.formatDateForDisplay(sd.value1);
      const date2 = this.formatDateForDisplay(sd.value2);
      const label = this.$t("article_put_online.stocked");
      return date2 !== ""
        ? `${label}: ${date1} → ${date2}`
        : `${label}: ${date1}`;
    },
    publicationDateDisplay() {
      const pd = this.savedFilters.publicationDate;
      if (!pd || !pd.value1) return "";
      const date1 = this.formatDateForDisplay(pd.value1);
      const date2 = this.formatDateForDisplay(pd.value2);
      const label = this.$t("article_put_online.published");
      return date2 !== ""
        ? `${label}: ${date1} → ${date2}`
        : `${label}: ${date1}`;
    },
    publishDate() {
      if (!this.articleData?.activities?.length) return null;

      // On trouve la dernière activité 'published'
      const publishActivity = [...this.articleData.activities]
        .reverse()
        .find((activity) => activity.event === "published");

      return publishActivity
        ? this.formatDateForDisplay(publishActivity.createdAt)
        : "-";
    },
    hasActiveFilters() {
      return (
        (this.savedFilters.brands &&
          this.savedFilters.brands.values.length > 0) ||
        (this.currentTab && this.currentTab !== "all")
      );
    },

    getBrandsLabel() {
      if (!this.savedFilters.brands) return "";

      const brandFilter = (this.tableFilters || []).find(
        (filter) => filter.key === "brand"
      );

      const brandLabels = this.savedFilters.brands.values.map((brandId) => {
        const brand = brandFilter?.options?.find(
          (opt) => opt.id.toString() === brandId.toString()
        );
        return brand?.label || brandId;
      });

      return brandLabels.join(", ");
    },

    isOnSale() {
      return this.article.status === "on_sale";
    },
    isUnpublished() {
      return this.article.status === "unpublished";
    },
    isDraft() {
      return this.article.status === "draft";
    },
    isToPublish() {
      return this.article.status === "to_publish";
    },
    isToCorrect() {
      return this.article.status === "to_correct";
    },
  },

  methods: {
    ...mapActions([
      "fetchArticle",
      "fetchArticles",
      "updateArticle",
      "fetchArticleReport",
      "updateArticleState",
      "syncArticleWithCore",
    ]),
    // `beforeRouteLeave` peut annuler la navigation (modale "Rester sur la
    // page") : vue-router rejette alors la promesse avec une NavigationAborted
    // que le patch global de `router/index.js` re-throw. C'est ici un cas
    // nominal, pas une erreur — on l'absorbe pour ne pas polluer la console.
    navigateTo(location) {
      return this.$router.push(location).catch((err) => {
        // vue-router marque ses erreurs avec `_isRouter` et un `type`
        // numérique (NavigationFailureType.aborted === 4), sans `name`.
        const NAVIGATION_ABORTED = 4;
        const isAborted =
          err && err._isRouter && err.type === NAVIGATION_ABORTED;

        if (!isAborted) {
          throw err;
        }
      });
    },

    goBack() {
      const savedFilters = localStorage.getItem("savedTableFilters");
      if (savedFilters) {
        let { filters, tab, page, limit, search, selectedItems } =
          JSON.parse(savedFilters);

        if (tab === "unpublished" || tab === "draft") {
          tab = "all";
        }

        this.navigateTo({
          name: "config-website-catalogues-articles-listing-all-bis",
          query: {
            filters,
            tab,
            page,
            limit,
            search, // Ajout du terme de recherche dans l'URL
            "currency[eq]": this.currentCurrency,
            selectedItems,
          },
        });

        // localStorage.removeItem("savedTableFilters");
      } else {
        this.navigateTo({
          name: "config-website-catalogues-articles-listing-all-bis",
        });
      }
    },
    handleInput(value) {
      this.$emit("input", value);
    },

    toggleMoreActions() {
      this.isMoreActionsOpen = !this.isMoreActionsOpen;
    },

    closeMoreActions() {
      this.isMoreActionsOpen = false;
    },

    handleEscape(event) {
      if (event.key !== "Escape") return;

      // Échap sur la modale équivaut à "Rester sur la page" : on ne veut pas
      // laisser la navigation suspendue sans réponse.
      if (this.isLeaveConfirmOpen) {
        this.cancelLeave();
        return;
      }

      this.closeMoreActions();
    },

    // L'utilisateur assume la perte de ses modifications : on rejoue la
    // navigation suspendue par `beforeRouteLeave`.
    confirmLeave() {
      const next = this.pendingLeaveNext;

      this.isLeaveConfirmOpen = false;
      this.pendingLeaveNext = null;

      if (next) next();
    },

    cancelLeave() {
      const next = this.pendingLeaveNext;

      this.isLeaveConfirmOpen = false;
      this.pendingLeaveNext = null;

      // `next(false)` annule la navigation et laisse l'utilisateur sur la page
      // avec ses saisies intactes.
      if (next) next(false);
    },

    runMoreAction(action) {
      if (action.disabled) return;

      this.closeMoreActions();
      action.handler();
    },

    // Recharge le tampon depuis le store pour la langue courante. Appelé au
    // chargement d'un article et après un enregistrement réussi.
    resetDraft() {
      const metadata = this.articleData?.metadata?.[this.currentLang];

      this.draft = {
        title: metadata?.title || "",
        description: this.toEditorHtml(metadata?.description),
        material: metadata?.composition || "",
        photos: this.clonePhotos(this.article.photos),
      };
      this.draftBaseline = {
        ...this.draft,
        photos: this.clonePhotos(this.draft.photos),
      };
      this.pendingMetadataByLang = {};
    },

    // vue2-editor injecte le HTML brut (innerHTML) au lieu de passer par l'API Quill
    // Quill confond alors les <div> avec son bloc racine (même balise) et plante.
    toEditorHtml(html) {
      return (html || "").replace(/<(\/?)div\b/gi, "<$1p");
    },

    clonePhotos(photos) {
      return (photos || []).map((photo) => ({ ...photo }));
    },

    // Seuls l'ordre des photos visibles et le masquage sont modifiables :
    // c'est ce couple qui détermine si les photos sont "sales".
    //
    // Les masquées sont sérialisées à part et triées par id : PhotosManager
    // les regroupe en fin de tableau au montage, alors que le back peut les
    // renvoyer intercalées. Les indexer dans la séquence rendrait la signature
    // différente de la baseline sans la moindre action utilisateur, ce qui
    // grisait Publier et la navigation dès l'ouverture de la fiche.
    serializePhotos(photos) {
      const isHidden = (photo) => photo.position === -1;
      const key = (photo, position) =>
        `${photo.id}:${position}:${photo.deleted ?? !!photo.deletedAt}`;

      const all = photos || [];
      const visible = all
        .filter((photo) => !isHidden(photo))
        .map((photo, index) => key(photo, index));
      const hidden = all
        .filter(isHidden)
        .map((photo) => key(photo, -1))
        .sort();

      return [...visible, ...hidden].join("|");
    },

    // Avant de changer de langue, on met de côté les saisies de la langue
    // quittée pour ne pas les perdre (le tampon est mono-langue).
    handleLangChange(langCode) {
      if (langCode === this.currentLang) return;

      this.stashCurrentLangChanges();
      this.currentLang = langCode;

      const pending = this.pendingMetadataByLang[langCode];
      const metadata = this.articleData?.metadata?.[langCode];

      this.draft = {
        ...this.draft,
        title: pending?.title ?? metadata?.title ?? "",
        description: pending?.description ?? this.toEditorHtml(metadata?.description),
        material: pending?.composition ?? metadata?.composition ?? "",
      };
      this.draftBaseline = {
        ...this.draftBaseline,
        title: metadata?.title || "",
        description: this.toEditorHtml(metadata?.description),
        material: metadata?.composition || "",
      };
    },

    stashCurrentLangChanges() {
      const changed = {};
      const fieldMap = {
        title: "title",
        description: "description",
        material: "composition",
      };

      Object.entries(fieldMap).forEach(([field, metadataKey]) => {
        if (this.draft[field] !== this.draftBaseline[field]) {
          changed[metadataKey] = this.draft[field];
        }
      });

      // On remplace l'entrée au lieu de la fusionner : `changed` décrit déjà
      // l'écart complet de la langue par rapport à `draftBaseline`. Un merge
      // ressusciterait un champ que l'utilisateur vient de remettre à sa
      // valeur d'origine, puisque celui-ci sort simplement de `changed`.
      if (Object.keys(changed).length) {
        this.$set(this.pendingMetadataByLang, this.currentLang, changed);
      } else {
        this.$delete(this.pendingMetadataByLang, this.currentLang);
      }
    },

    // Réaligne la date de dernière mise à jour sur maintenant, sans toucher au
    // reste de l'article : on repart de l'état courant du store pour ne perdre
    // ni les photos ni les erreurs taggées.
    touchUpdatedAt() {
      if (!this.articleData) return;

      this.$store.commit("SET_ARTICLE", {
        ...this.articleData,
        articleCard: {
          ...this.articleData.articleCard,
          updatedAt: new Date().toISOString(),
        },
      });
    },

    // Envoie en une seule fois toutes les modifications en attente : le
    // metadata (toutes langues confondues) et les photos partent dans le même
    // PUT. Les séparer en deux requêtes lançait deux synchronisations
    // concurrentes du même tradeIn côté back.
    async handleSave() {
      if (!this.hasUnsavedChanges || this.saveLoading) return;

      this.saveLoading = true;
      const currentLangBackup = this.currentLang;

      try {
        this.stashCurrentLangChanges();

        const photosChanged =
          this.serializePhotos(this.draft.photos) !==
          this.serializePhotos(this.draftBaseline.photos);
        const metadataChanged = Object.keys(this.pendingMetadataByLang).length > 0;

        // `stashCurrentLangChanges` vient peut-être de retirer le dernier
        // changement en attente (champ retapé à sa valeur d'origine) : il n'y a
        // alors plus rien à envoyer, et on réaligne le tampon plutôt que de
        // laisser un résidu de stash derrière nous.
        if (!photosChanged && !metadataChanged) {
          this.resetDraft();
          return;
        }

        const payload = { id: this.article.id };

        if (metadataChanged) {
          const metadata = { ...this.articleData.metadata };

          Object.entries(this.pendingMetadataByLang).forEach(
            ([lang, fields]) => {
              metadata[lang] = { ...(metadata[lang] || {}), ...fields };
            }
          );

          payload.metadata = metadata;
        }

        // `photos` reste absent du body quand rien n'a bougé : le back le
        // traite comme optionnel et ne retouche alors pas les photos.
        if (photosChanged) {
          const visible = this.draft.photos.filter(
            (photo) => photo.position !== -1
          );
          const hidden = this.draft.photos.filter(
            (photo) => photo.position === -1
          );

          payload.photos = [
            ...visible.map((photo, index) => ({
              id: photo.id,
              position: index,
              deleted: photo.deleted ?? !!photo.deletedAt,
              url: photo.url,
            })),
            ...hidden.map((photo) => ({
              id: photo.id,
              position: -1,
              deleted: photo.deleted ?? !!photo.deletedAt,
              url: photo.url,
            })),
          ];
        }

        // `notify: false` : cette page affiche son propre message ci-dessous,
        // celui du store ferait doublon.
        await this.updateArticle({ ...payload, notify: false });

        // La date de dernière mise à jour affichée vient de
        // `articleCard.updatedAt`, que les réponses de sauvegarde ne
        // rafraîchissent pas toujours. On la force comme le font déjà les
        // autres actions de la page (publication, signalement, sync).
        this.touchUpdatedAt();

        // Filet de sécurité : le watcher `articleData` ne touche plus à la
        // langue tant que l'id ne change pas, mais on garantit explicitement
        // que l'utilisateur reste sur l'onglet où il travaillait.
        this.currentLang = currentLangBackup;
        // `resetDraft` réaligne draft et baseline (texte + photos) sur le
        // store, et purge `pendingMetadataByLang`.
        this.resetDraft();

        // Un seul message pour un seul enregistrement, quel qu'en soit le
        // contenu : le texte et les photos partent désormais ensemble.
        this.$refs.notification?.open({
          text: this.$t("article_put_online.notification_popin.save_success"),
          color: "green",
          confirmation: false,
          duration: 3000,
        });
      } catch (error) {
        console.warn("Erreur lors de l'enregistrement de l'article:", error);
        this.currentLang = currentLangBackup;

        this.$refs.notification?.open({
          text: this.$t("article_put_online.notification_popin.save_error"),
          color: "red",
          confirmation: false,
          duration: 3000,
        });
      } finally {
        this.saveLoading = false;
      }
    },

    getStatusFromTab(tab) {
      return tab;
    },

    transformFiltersForAPI(filters) {
      const apiFilters = {};
      if (!filters) return apiFilters;

      // Filtre pour la marque
      if (filters.brands) {
        const operator = filters.brands.operator === "inclus" ? "in" : "nin";
        apiFilters[`brand[${operator}]`] = filters.brands.values.join(",");
      }

      // Filtre pour l'état de publication (ou on utilise le tab actif)
      if (filters.publishStates) {
        const operator =
          filters.publishStates.operator === "inclus" ? "in" : "nin";
        apiFilters[`publishState[${operator}]`] =
          filters.publishStates.values.join(",");
      } else if (this.currentTab !== "all") {
        apiFilters["publishState[in]"] = this.currentTab;
      }

      // Filtre pour les canaux de vente
      if (filters.channels) {
        const operator = filters.channels.operator === "inclus" ? "in" : "nin";
        apiFilters[`channel[${operator}]`] = filters.channels.values.join(",");
      }

      // Filtre pour la date de mise en stock
      if (filters.stockDate) {
        const sd = filters.stockDate;
        if (sd.value1 && sd.value2) {
          // Si deux dates sont renseignées, on envoie gte et lte
          apiFilters["stockAt[gte]"] = sd.value1;
          apiFilters["stockAt[lte]"] = sd.value2;
        } else if (sd.value1) {
          // Si une seule date est renseignée, on se base sur l'opérateur
          if (sd.operator === "supérieur à") {
            apiFilters["stockAt[gte]"] = sd.value1;
          } else if (sd.operator === "inférieur à") {
            apiFilters["stockAt[lte]"] = sd.value1;
          }
        }
      }

      // Filtre pour la date de publication
      if (filters.publicationDate) {
        const pd = filters.publicationDate;
        if (pd.value1 && pd.value2) {
          apiFilters["publishedAt[gte]"] = pd.value1;
          apiFilters["publishedAt[lte]"] = pd.value2;
        } else if (pd.value1) {
          if (pd.operator === "supérieur à") {
            apiFilters["publishedAt[gte]"] = pd.value1;
          } else if (pd.operator === "inférieur à") {
            apiFilters["publishedAt[lte]"] = pd.value1;
          }
        }
      }

      // Filtre pour les erreurs
      if (filters.errors) {
        const operator = filters.errors.operator === "inclus" ? "in" : "nin";
        apiFilters[`reportingError[${operator}]`] =
          filters.errors.values.join(",");
      }

      return apiFilters;
    },

    applyDefaultSort(queryParams) {
      // Applique le tri par défaut selon l'onglet actif (même logique que la page listing)
      if (this.currentTab === "on_sale") {
        queryParams["sort[_publishedDate]"] = "desc";
      } else if (this.currentTab === "to_correct") {
        queryParams["sort[ac.updatedAt]"] = "desc";
      } else {
        // all, to_publish
        queryParams["sort[_stockedDate]"] = "desc";
      }
    },

    async goToNextProduct() {
      try {
        const queryParams = {
          page: 1,
          limit: this.savedLimit || 100,
          "currency[eq]": this.currentCurrency,
          ...this.transformFiltersForAPI(this.savedFilters),
        };

        // Appliquer le tri sauvegardé ou le tri par défaut
        if (this.savedSort) {
          Object.assign(queryParams, this.savedSort);
        } else {
          this.applyDefaultSort(queryParams);
        }

        // Ajouter la recherche si présente
        if (this.savedSearch) {
          queryParams.search = this.savedSearch;
        }

        const data = await this.fetchArticles(queryParams);
        if (data?.items?.length) {
          const currentIndex = data.items.findIndex(
            (item) => item.id === this.articleId
          );

          if (currentIndex === data.items.length - 1) {
            const nextProduct = data.items[0];

            this.$store.commit("SET_NOTIFICATION_MESSAGE", {
              text: "Retour au premier article",
              color: "blue",
              confirmation: false,
            });

            this.$router.push({
              name: "config-website-catalogues-articles-details-bis",
              params: { id: nextProduct.id },
            });
          } else {
            const nextProduct = data.items[currentIndex + 1];
            this.$router.push({
              name: "config-website-catalogues-articles-details-bis",
              params: { id: nextProduct.id },
            });
          }
        }
      } catch (error) {
        console.error("Erreur:", error);
      }
    },
    async goToPreviousProduct() {
      try {
        // Construire la requête avec les filtres actifs
        const queryParams = {
          page: 1,
          limit: this.savedLimit || 100,
          "currency[eq]": this.currentCurrency,
          ...this.transformFiltersForAPI(this.savedFilters),
        };

        // Appliquer le tri sauvegardé ou le tri par défaut
        if (this.savedSort) {
          Object.assign(queryParams, this.savedSort);
        } else {
          this.applyDefaultSort(queryParams);
        }

        // Ajouter la recherche si présente
        if (this.savedSearch) {
          queryParams.search = this.savedSearch;
        }

        const data = await this.fetchArticles(queryParams);
        if (data?.items?.length) {
          // Si un filtre de statut est appliqué, nous le récupérons
          // Sinon, on utilise la liste complète.
          const expectedStatus =
            this.savedFilters?.publishStates?.values?.[0] || null;
          const filteredItems = expectedStatus
            ? data.items.filter((item) => item.status === expectedStatus)
            : data.items;

          if (!filteredItems.length) {
            // Aucun article ne correspond au filtre de statut actif.
            // Redirigee vers le premier article de la liste complète.
            this.$router.push({
              name: "config-website-catalogues-articles-details-bis",
              params: { id: data.items[0].id },
            });
            return;
          }

          // Trouve l'indice de l'article courant dans la liste filtrée
          const currentIndex = filteredItems.findIndex(
            (item) => item.id === this.articleId
          );

          let targetProduct;
          if (currentIndex === -1) {
            // Si l'article courant n'est pas trouvé (son statut a pu être modifié),
            // alors on choisit le premier article de la liste filtrée.
            targetProduct = filteredItems[0];
          } else if (currentIndex === 0) {
            // Si l'article courant est le premier, on peut choisir le dernier pour une navigation circulaire.
            targetProduct = filteredItems[filteredItems.length - 1];
          } else {
            // Sinon, on prend le produit précédent dans la liste filtrée.
            targetProduct = filteredItems[currentIndex - 1];
          }

          this.$router.push({
            name: "config-website-catalogues-articles-details-bis",
            params: { id: targetProduct.id },
          });
        }
      } catch (error) {
        console.error(
          "Erreur lors de la navigation vers le produit précédent:",
          error
        );
      }
    },
    getStatusFromState(state) {
      const stateMap = {
        to_publish: "À publier",
        on_sale: "En vente",
        unpublished: "Dépublié",
        to_correct: "À corriger",
        draft: "Brouillon",
      };
      return stateMap[state] || state;
    },

    async loadArticleDataId() {
      try {
        // Chargement de l'article
        const articleData = await this.fetchArticle(this.articleId);

        // Mettre à jour l'article dans le store
        this.$store.commit("SET_ARTICLE", articleData);

        // Si l'article est en état "to_correct", charger les raisons
        if (articleData?.articleCard?.state === "to_correct") {
          await this.$store.dispatch(
            "fetchArticleReport",
            articleData.articleCard.id
          );
        }
      } catch (error) {
        console.warn("Erreur lors du chargement de l'article:", error);
      }
    },

    // PhotosManager est en mode différé : il remonte le nouvel ordre / masquage
    // sans appeler l'API. On ne fait que mettre à jour le tampon.
    handlePhotosUpdate(updatedPhotos) {
      this.draft.photos = this.clonePhotos(updatedPhotos);
    },
    adjustHeight(element) {
      // Sauvegarde la valeur de scroll
      const scrollPos = window.pageYOffset;

      element.style.height = "24px";

      if (element.scrollHeight > element.clientHeight) {
        element.style.height = element.scrollHeight + "px";
      }

      // On ajoute un petit padding pour éviter les coupures de texte
      const computed = window.getComputedStyle(element);
      const padding =
        parseInt(computed.paddingTop) + parseInt(computed.paddingBottom);

      // On définit la hauteur en prenant en compte le padding
      element.style.height = element.scrollHeight + padding + "px";

      // Restaure la position de scroll
      window.scrollTo(0, scrollPos);
    },

    autoResize(event) {
      this.adjustHeight(event.target);
    },
    openReportModal() {
      this.isReportModalOpen = true;
    },

    openAiPhotoModal() {
      this.isAiPhotoModalOpen = true;
    },

    closeAiPhotoModal() {
      this.isAiPhotoModalOpen = false;
    },

    async handleAiPhotoApplied() {
      await this.loadArticleDataId();
    },
    
    triggerReportSubmit() {
      // Déclencher la soumission du formulaire enfant
      if (this.$refs.reportForm) {
        this.$refs.reportForm.handleSubmit();
      }
    },

    async handleReportSubmit(formData) {
      try {
        if (!this.articleData?.articleCard?.id) {
          throw new Error("ID de l'article non disponible");
        }

        const articleCardId = this.articleData.articleCard.id;
        
        // Utiliser directement les données structurées du composant enfant
        const { toAdd, toRemove, selectedErrors } = formData;
        
        console.log('📝 Processing report changes:', {
          toAdd: toAdd?.length || 0,
          toRemove: toRemove?.length || 0,
          selectedTotal: selectedErrors?.length || 0
        });

        // Créer les requêtes parallèles
        const requests = [];

        // Requêtes d'ajout (POST)
        if (toAdd && toAdd.length > 0) {
          toAdd.forEach(error => {
            requests.push(
              this.$store.dispatch("submitReport", {
                id: articleCardId,
                reason: error.reason,
                description: error.description,
              })
            );
          });
        }

        // Requêtes de suppression (DELETE)
        if (toRemove && toRemove.length > 0) {
          toRemove.forEach(error => {
            requests.push(
              this.$store.dispatch("deleteReport", {
                articleCardId: articleCardId,
                reportId: error.id,
              })
            );
          });
        }

        // Exécuter toutes les requêtes
        if (requests.length > 0) {
          await Promise.all(requests);
        }
        
        // IMPORTANT: Rafraîchir les erreurs depuis l'API pour obtenir les nouveaux IDs
        // (met à jour reportReasons via SET_ARTICLE_REASONS, sans toucher aux photos)
        await this.$store.dispatch("fetchArticleReport", articleCardId);

        // Déterminer le nouveau statut selon la règle backend
        const newState = selectedErrors.length === 0 ? "draft" : "to_correct";

        // Créer le nouvel objet article avec l'état final.
        // On repart de l'état COURANT du store (this.articleData) pour préserver
        // explicitement les photos (et leur masquage) : sans ce spread ciblé,
        // SET_ARTICLE réécrirait l'objet et le masquage des photos sauterait.
        const updatedArticleData = {
          ...this.articleData,
          photos: this.articleData.photos, // conserver le masquage des photos
          articleCard: {
            ...this.articleData.articleCard,
            state: newState,
            updatedAt: new Date().toISOString(),
          },
          reportReasons: selectedErrors, // État final sélectionné
        };

        // Mettre à jour l'article dans le store
        this.$store.commit("SET_ARTICLE", updatedArticleData);

        // Le SET_ARTICLE ci-dessus remplace l'objet sans changer son id : le
        // watcher `articleData` ne se déclenche donc pas et la baseline du
        // tampon reste calée sur l'objet précédent. Sans ce reset, quitter la
        // page après un simple signalement ouvrait la modale "Modifications
        // non enregistrées" alors que rien n'avait été saisi.
        this.resetDraft();

        this.isReportModalOpen = false;
      } catch (error) {
        console.error("Erreur lors du signalement:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t(
            "article_put_online.notification_popin.error_reported_error"
          ),
          color: "red",
          confirmation: true,
          duration: 3000,
        });
      }
    },
    handleReportClose() {
      this.isReportModalOpen = false;
      // Le composant gère maintenant son état interne automatiquement
    },
    async handlePublish() {
      try {
        await this.updateArticleState({
          ids: [this.articleId],
          toStatus: "on_sale",
        });

        // Recharger l'article pour avoir le nouveau statut
        await this.loadArticleDataId();

        // Force la mise à jour de l'UI en modifiant l'article
        if (this.articleData && this.articleData.articleCard) {
          // Créer une copie pour déclencher la réactivité
          const updatedArticleData = { ...this.articleData };
          updatedArticleData.articleCard = {
            ...updatedArticleData.articleCard,
            updatedAt: new Date().toISOString(), // On force une mise à jour immédiate de l'UI
          };
          this.$store.commit("SET_ARTICLE", updatedArticleData);
        }

        // Mettre à jour les filtres sauvegardés
        const savedFilters = localStorage.getItem("savedTableFilters");
        if (savedFilters) {
          const parsedFilters = JSON.parse(savedFilters);
          this.savedFilters = parsedFilters.filters
            ? JSON.parse(parsedFilters.filters)
            : {};
          this.currentTab = parsedFilters.tab || "all";
        }

        // Si on est sur un filtre de statut spécifique, on met à jour la liste filtrée
        if (this.currentTab && this.currentTab !== "all") {
          const queryParams = {
            page: 1,
            limit: 100,
            "currency[eq]": this.currentCurrency,
            "publishState[in]": this.currentTab,
          };

          if (this.savedFilters.brands) {
            const operator =
              this.savedFilters.brands.operator === "inclus" ? "in" : "nin";
            queryParams[`brand[${operator}]`] =
              this.savedFilters.brands.values.join(",");
          }

          await this.fetchArticles(queryParams);
        }

        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t("article_put_online.notification_popin.item_published"),
          color: "green",
          confirmation: true,
          duration: 3000,
        });
      } catch (error) {
        console.error("Erreur lors de la publication:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t(
            "article_put_online.notification_popin.item_published_error"
          ),
          color: "red",
          confirmation: true,
          duration: 3000,
        });
      }
    },

    async handleUnpublish() {
      try {
        await this.updateArticleState({
          ids: [this.articleId],
          toStatus: "unpublished",
        });

        // Recharger l'article pour avoir le nouveau statut
        await this.loadArticleDataId();

        // Force la mise à jour de l'UI en modifiant l'article
        if (this.articleData && this.articleData.articleCard) {
          // Créer une copie pour déclencher la réactivité
          const updatedArticleData = { ...this.articleData };
          updatedArticleData.articleCard = {
            ...updatedArticleData.articleCard,
            updatedAt: new Date().toISOString(),
          };
          this.$store.commit("SET_ARTICLE", updatedArticleData);
        }

        // Même logique de mise à jour des filtres que pour handlePublish
        const savedFilters = localStorage.getItem("savedTableFilters");
        if (savedFilters) {
          const parsedFilters = JSON.parse(savedFilters);
          this.savedFilters = parsedFilters.filters
            ? JSON.parse(parsedFilters.filters)
            : {};
          this.currentTab = parsedFilters.tab || "all";
        }

        if (this.currentTab && this.currentTab !== "all") {
          const queryParams = {
            page: 1,
            limit: 100,
            "currency[eq]": this.currentCurrency,
            "publishState[in]": this.currentTab,
          };

          if (this.savedFilters.brands) {
            const operator =
              this.savedFilters.brands.operator === "inclus" ? "in" : "nin";
            queryParams[`brand[${operator}]`] =
              this.savedFilters.brands.values.join(",");
          }

          await this.fetchArticles(queryParams);
        }
      } catch (error) {
        console.error("Erreur lors de la dépublication:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t(
            "article_put_online.notification_popin.item_unpublished_error"
          ),
          color: "red",
          confirmation: true,
          duration: 3000,
        });
      }
    },

    async handleSetToPublish() {
      try {
        await this.updateArticleState({
          ids: [this.articleId],
          toStatus: "to_publish",
        });

        // Recharger l'article pour avoir le nouveau statut
        await this.loadArticleDataId();

        // Force la mise à jour de l'UI en modifiant l'article
        if (this.articleData && this.articleData.articleCard) {
          const updatedArticleData = { ...this.articleData };
          updatedArticleData.articleCard = {
            ...updatedArticleData.articleCard,
            updatedAt: new Date().toISOString(),
          };
          this.$store.commit("SET_ARTICLE", updatedArticleData);
        }

        // Mettre à jour les filtres sauvegardés
        const savedFilters = localStorage.getItem("savedTableFilters");
        if (savedFilters) {
          const parsedFilters = JSON.parse(savedFilters);
          this.savedFilters = parsedFilters.filters
            ? JSON.parse(parsedFilters.filters)
            : {};
          this.currentTab = parsedFilters.tab || "all";
        }

        if (this.currentTab && this.currentTab !== "all") {
          const queryParams = {
            page: 1,
            limit: 100,
            "currency[eq]": this.currentCurrency,
            "publishState[in]": this.currentTab,
          };

          if (this.savedFilters.brands) {
            const operator =
              this.savedFilters.brands.operator === "inclus" ? "in" : "nin";
            queryParams[`brand[${operator}]`] =
              this.savedFilters.brands.values.join(",");
          }

          await this.fetchArticles(queryParams);
        }

        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t("article_put_online.notification_popin.item_to_publish"),
          color: "green",
          confirmation: true,
          duration: 3000,
        });
      } catch (error) {
        console.error("Erreur lors du passage à publier:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t(
            "article_put_online.notification_popin.item_to_publish_error"
          ),
          color: "red",
          confirmation: true,
          duration: 3000,
        });
      }
    },

    async handleSync() {
      try {
        this.syncLoading = true;
        await this.syncArticleWithCore(this.articleId);

        // Recharger l'article pour avoir les données synchronisées
        await this.loadArticleDataId();

        // Force la mise à jour de l'UI en modifiant l'article
        if (this.articleData && this.articleData.articleCard) {
          const updatedArticleData = { ...this.articleData };
          updatedArticleData.articleCard = {
            ...updatedArticleData.articleCard,
            updatedAt: new Date().toISOString(),
          };
          this.$store.commit("SET_ARTICLE", updatedArticleData);
        }
      } catch (error) {
        console.error("Erreur lors de la synchronisation:", error);
      } finally {
        this.syncLoading = false;
      }
    },
  },

  mounted() {
    // Initialiser la hauteur pour tous les textarea au chargement
    this.$nextTick(() => {
      const textareas = document.querySelectorAll("textarea");
      textareas.forEach(this.adjustHeight);
    });

    document.addEventListener("keydown", this.handleEscape);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEscape);

    // Sécurité : ne jamais laisser une navigation suspendue sans réponse,
    // sinon le routeur resterait bloqué pour toute la session.
    if (this.pendingLeaveNext) {
      this.pendingLeaveNext(false);
      this.pendingLeaveNext = null;
    }
  },

  created() {
    // Charger les filtres
    this.$store.dispatch("getTableFilters", {
      name: "articles",
      url: "new-articles",
    });

    // Récupérer les filtres sauvegardés
    const savedFilters = localStorage.getItem("savedTableFilters");
    if (savedFilters) {
      const { filters, tab, sort, search, limit } = JSON.parse(savedFilters);
      this.savedFilters = filters ? JSON.parse(filters) : {};
      this.currentTab = tab || "all";
      this.savedSort = sort || null;
      this.savedSearch = search || null;
      this.savedLimit = limit ? parseInt(limit, 10) : 100;
    }
  },

  // Les modifications ne partent plus au fil de l'eau : quitter la page sans
  // enregistrer les perd. On demande confirmation plutôt que de les abandonner
  // silencieusement.
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedChanges) {
      next();
      return;
    }

    // On suspend la navigation : elle sera rejouée par la modale de
    // confirmation, via `confirmLeave` ou `cancelLeave`.
    this.pendingLeaveNext = next;
    this.isLeaveConfirmOpen = true;
  },

  watch: {
    articleId: {
      immediate: true,
      async handler(newId) {
        if (newId) {
          await this.loadArticleDataId();
        }
      },
    },
    // Volontairement non-deep : ce watcher ne doit réagir qu'au remplacement de
    // l'article (changement de fiche), pas à chaque SET_ARTICLE, sous peine de
    // réinitialiser la langue courante et d'écraser les saisies en attente.
    articleData: {
      immediate: true,
      handler(newArticleData, oldArticleData) {
        if (!newArticleData) return;

        const isSameArticle =
          oldArticleData && oldArticleData.id === newArticleData.id;

        if (!isSameArticle) {
          if (newArticleData?.brand?.defaultLocaleCode) {
            this.currentLang = newArticleData.brand.defaultLocaleCode;
          }
          this.resetDraft();
        }
      },
    },
    // Le tampon est mono-langue : on reporte en continu les écarts de la langue
    // affichée dans `pendingMetadataByLang`, sinon revenir manuellement à la
    // valeur d'origine laisserait un résidu et `hasUnsavedChanges` resterait
    // vrai à tort (tous les CTA grisés sans raison).
    draft: {
      deep: true,
      handler() {
        if (this.currentLang) {
          this.stashCurrentLangChanges();
        }
      },
    },
    "draft.title"() {
      this.$nextTick(() => {
        if (this.$refs.titleTextarea) {
          this.adjustHeight(this.$refs.titleTextarea);
        }
      });
    },
    "draft.material"() {
      this.$nextTick(() => {
        if (this.$refs.materialTextarea) {
          this.adjustHeight(this.$refs.materialTextarea);
        }
      });
    },
  },
};
</script>

<style scoped>
.article-details {
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header__left,
.header__right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn:hover {
  opacity: 0.6;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
}

.btn-grey {
  color: #666;
  background-color: #f7f7f7;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-red {
  color: #fe3737;
  background-color: #fff;
  border: none;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #ffeeed;
    color: #fe3737;
    border-color: #fe3737;
    opacity: 1;
  }
  &:disabled:hover {
    opacity: 0.5;
    color: #fe3737;
    border: 1px solid #e0e0e0;
    background-color: #fff;
  }
}

.btn-primary {
  background: #000;
  color: white;
  border: none;
  border: 1px solid #000;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: #fff;
  color: #000;
  border: 1px solid #e0e0e0;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f5f5f5;
  }
}

.leave-modal__message {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.btn-icon-only {
  padding: 8px 10px;
  justify-content: center;

  svg {
    width: 14px;
    height: 14px;
  }
}

.more-actions {
  position: relative;
}

.more-actions__menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 220px;
  background-color: #fff;
  box-shadow: 0 4px 24px 0 rgba(25, 25, 25, 0.1);
  border-radius: 6px;
  padding: 8px;
  z-index: 1000;
}

.more-actions__option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 36px;
  padding: 0 8px;
  border: none;
  background: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #000;
  text-align: left;
  white-space: nowrap;

  &:hover {
    background-color: #f8f8f8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: transparent;
  }

  &.more-actions__option--warning {
    color: #fe3737;
    --svg-color: #fe3737;
  }
}

.more-actions__icon {
  display: flex;
  flex: 0 0 16px;

  svg {
    width: 16px;
    height: 16px;
  }
}

.ai-photo-beta-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b4fc4;
  background: #eeecff;
  border-radius: 4px;
}
.tabs-wrapper {
  background-color: #f1efec;
  padding: 2px;
  padding-bottom: 4px;
  border-radius: 8px;
  margin-bottom: 24px;
  width: fit-content;
}

.tabs {
  display: flex;
}

.tab-button {
  padding: 12px 24px;
  border: none;
  background: none;
  cursor: pointer;
  position: relative;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-button.active {
  color: #000;
  margin-bottom: -1px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
}

.lang-flag {
  width: 20px;
  height: 20px;
  object-fit: cover;
  border-radius: 4px;
}

.lang-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
}

.lang-btn.active {
  background: #f5f5f5;
  border-color: #000;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
}

.section {
  background: white;
  border-radius: 8px;
  border: 1px solid #e1e1e1;
  margin-bottom: 24px;
}

.section_header {
  padding: 24px;
  background: #f7f7f7;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: 1px solid #e1e1e1;
}

.section_containt {
  padding: 24px;
}

.section h2 {
  font-size: 18px;
}

.section_containt-photos {
  padding-top: 10px;
}

.form-group {
  margin-bottom: 16px;
}

.input-container {
  position: relative;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 8px 16px;
}

.input-container label {
  display: block;
  font-size: 12px;
  color: #757575;
  margin-bottom: 4px;
}

.input-container input,
.input-container textarea {
  width: 100%;
  border: none;
  font-size: 14px;
  color: #000;
  background: transparent;
  padding: 0;
}

.input-container input:focus,
.input-container textarea:focus {
  outline: none;
}

.input-container textarea {
  width: 100%;
  min-height: 24px;
  border: none;
  font-size: 14px;
  color: #000;
  background: transparent;
  padding: 0;
  resize: none;
  overflow-y: hidden; /* cache la barre de défilement */
  line-height: 1.5;
  display: block;
  box-sizing: border-box;
}

.input-container.disabled {
  background-color: #f5f5f5;
  border-color: #e0e0e0;
}

.input-container input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  color: #666;
}

.input-container :deep(.quillWrapper .ql-snow.ql-toolbar .ql-formats) {
  margin-bottom: 0;
}

.input-container :deep(.ql-toolbar.ql-snow) {
  display: flex;
  justify-content: flex-end;
  padding: 4px 0;
}

.input-container :deep(.ql-editor ul),
.input-container :deep(.ql-editor ol) {
  padding-left: 0;
}
.input-container input:disabled + label {
  color: #666;
}

.input-container :deep(.ql-container) {
  border: none;
  font-size: 14px;
}

.input-container :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e8e8e8;
  padding: 8px 0;
}

.input-container :deep(.ql-editor) {
  padding: 12px 0;
  font-size: 14px;
  min-height: fit-content;
  color: #000 !important;
}

.input-container :deep(.ql-editor p) {
  color: #000 !important;
}
.input-container :deep(.ql-editor p:not(.ql-direction-rtl)) {
  color: #000 !important;
}
.input-container :deep(.ql-toolbar.ql-snow) {
  padding: 4px 0;
}

.input-container :deep(.ql-toolbar .ql-picker-label) {
  font-size: 12px;
  padding: 0 4px;
}

.input-container :deep(.ql-toolbar button) {
  width: 24px;
  height: 24px;
  padding: 2px;
}

.input-container :deep(.quillWrapper .ql-snow.ql-toolbar button svg) {
  /* Ajuster la taille des icônes dans les boutons */
  width: 16px;
  height: 16px;
}

/* Pour enlever les flèches des inputs number */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Pour Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

.photo-item {
  position: relative;
  border-radius: 4px;
  overflow: hidden;
}

.photo-item img {
  width: 100%;
  height: auto;
  display: block;
}

.photo-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
}

.btn-icon {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
}

.info-card h3 {
  font-size: 16px;
  margin-bottom: 16px;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  align-items: center;

  .status-badge {
    width: fit-content;
  }
  &.report-reasons {
    align-items: flex-start;
  }
}

.info-row label {
  color: #666;
}

@media (max-width: 1024px) {
  .content-wrapper {
    grid-template-columns: 1fr;
  }
}
.side-content {
  position: sticky;
  top: 24px;
  max-height: calc(100vh - 48px); /* 2 * padding */
  overflow-y: auto;
  align-self: start;
}

.view-link {
  color: #2196f3;
  text-decoration: underline;
}

.tooltip-wrapper {
  position: relative;
}

.tooltip {
  position: absolute;
  display: none;
  background: white;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 12px;
  width: max-content;
  max-width: 300px;
  z-index: 1000;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
}

.tooltip::before {
  content: "";
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: white;
  border-left: 1px solid #e0e0e0;
  border-top: 1px solid #e0e0e0;
}

.tooltip-content {
  font-size: 12px;
  color: #666;
  line-height: 1.4;

  & > div {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-direction: column;
  }
}

.tooltip-content strong {
  color: #000;
  display: block;
  margin-bottom: 4px;
}

.tooltip-content div {
  margin-bottom: 4px;
}

.tooltip-wrapper:hover .tooltip {
  display: block;
}

.error-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
}
.info-update {
  display: flex;
  justify-content: flex-end;
}
.update-date {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #666;
  background-color: #f7f7f7;
  padding: 4px 8px;
  border-radius: 4px;
  margin-left: 8px;
}

.update-label {
  margin-right: 4px;
  font-weight: 500;
}

.update-value {
  color: #333;
}
</style>
